import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import en from './locales/en.json';
import vi from './locales/vi.json';

import {
  DEFAULT_LOCALE,
  LOCALE_STORAGE_KEY,
  SUPPORTED_LOCALES,
  isLocale,
  useLocaleStore,
  type Locale,
} from '@/stores/locale';

export const resources = {
  en: { translation: en },
  vi: { translation: vi },
} as const;

export type TranslationResources = (typeof resources)['vi'];

let initialized = false;

/**
 * Initialize i18next once. Safe to call multiple times — subsequent calls are
 * no-ops. Returns the i18n instance for callers that want to await readiness.
 */
export function initI18n() {
  if (initialized) return i18n;
  initialized = true;

  void i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources,
      fallbackLng: DEFAULT_LOCALE,
      supportedLngs: SUPPORTED_LOCALES,
      nonExplicitSupportedLngs: true,
      interpolation: { escapeValue: false },
      returnNull: false,
      detection: {
        order: ['localStorage', 'navigator', 'htmlTag'],
        caches: ['localStorage'],
        lookupLocalStorage: LOCALE_STORAGE_KEY,
      },
    });

  // Sync the persisted store with whatever i18next resolved.
  i18n.on('languageChanged', (lng) => {
    if (isLocale(lng)) {
      const store = useLocaleStore.getState();
      if (store.locale !== lng) store.setLocale(lng);
      if (typeof document !== 'undefined') {
        document.documentElement.lang = lng;
      }
    }
  });

  // If the persisted store already has a value, prefer it over the detector
  // result so reloads stay consistent.
  const persisted = useLocaleStore.getState().locale;
  if (isLocale(persisted) && i18n.language !== persisted) {
    void i18n.changeLanguage(persisted);
  }

  return i18n;
}

/** Programmatic locale change helper used by language switchers. */
export async function setAppLocale(locale: Locale) {
  useLocaleStore.getState().setLocale(locale);
  await i18n.changeLanguage(locale);
}

export default i18n;
